import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import Pagination from '../../../components/common/Pagination';
import { SERVICE_CATEGORIES } from '../../../config/ieumConfig';
import { db } from '../../../firebase/config';
import { formatUserName } from '../../../utils/userName';
import {
  collection, getDocs, doc, getDoc, addDoc, updateDoc,
  query, where, serverTimestamp,
} from 'firebase/firestore';

const labelCategory = (c) => SERVICE_CATEGORIES[c]?.label || c || '-';
const SUB_TYPE_LABELS = {
  normal: '일반이사', semi_pack: '반포장이사', pack: '포장이사', van: '용달이사',
  basic: '기본 입주청소', deep: '딥클리닝', both: '둘 다',
  internet_only: '인터넷 단독', internet_tv: '인터넷+TV',
  value: '가성비', modern: '모던', simple: '심플', custom: '직접상담',
};
const labelType = (cat, sub) => {
  const parts = [SERVICE_CATEGORIES[cat]?.label || cat, SUB_TYPE_LABELS[sub]].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : '-';
};

const REQ_STATUS = {
  pending: { label: '결제대기', color: '#F59E0B' },
  confirming: { label: '입금확인중', color: '#3B82F6' },
  completed: { label: '결제완료', color: '#22C55E' },
  cancelled: { label: '취소', color: '#9CA3AF' },
  rejected: { label: '거절', color: '#EF4444' },
};

const RECV_STATUS = {
  pending: { label: '결제대기', color: '#F59E0B' },
  in_progress: { label: '정산중', color: '#3B82F6' },
  confirming: { label: '확인대기', color: '#8B5CF6' },
  scheduled: { label: '결제예정', color: '#22C55E' },
};

const REQ_STATUS_ORDER = { pending: 0, confirming: 1, completed: 2, rejected: 3, cancelled: 4 };
const RECV_STATUS_ORDER = { pending: 0, in_progress: 1, confirming: 2, scheduled: 3 };

const PER_PAGE = 20;

const PartnerReceivable = () => {
  const [searchParams] = useSearchParams();
  const initialTab = ['requests', 'receivables'].includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'requests';
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['requests', 'receivables'].includes(t)) setTab(t);
  }, [searchParams]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // 결제 요청 탭 상태
  const [reqStatusFilter, setReqStatusFilter] = useState('all');
  const [reqPage, setReqPage] = useState(1);
  const [reqSortKey, setReqSortKey] = useState('createdAt');
  const [reqSortDir, setReqSortDir] = useState('desc');
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ partnerId: '', productName: '', amount: '', method: 'transfer', memo: '', receivableIds: [] });

  // 미수 탭 상태
  const [recvStatusFilter, setRecvStatusFilter] = useState('all');
  const [recvMethodFilter, setRecvMethodFilter] = useState('all');
  const [recvPage, setRecvPage] = useState(1);
  const [recvSortKey, setRecvSortKey] = useState('createdAt');
  const [recvSortDir, setRecvSortDir] = useState('desc');
  const [recvSelected, setRecvSelected] = useState([]);
  const [bulkMethodModal, setBulkMethodModal] = useState(false);
  const [bulkMethod, setBulkMethod] = useState('card');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqSnap, recvSnap, partnerSnap, partnerDocSnap] = await Promise.all([
        getDocs(query(collection(db, 'partner_payment_requests'))),
        getDocs(query(collection(db, 'partner_receivables'))),
        getDocs(query(collection(db, 'users'), where('role', '==', 'partner'))),
        getDocs(collection(db, 'partners')),
      ]);

      // partners 컬렉션 인덱스 (business_name 조인용)
      const partnerBizMap = new Map();
      partnerDocSnap.docs.forEach((d) => {
        const data = d.data();
        partnerBizMap.set(d.id, data.business_name || data.businessName || '');
      });

      const partnerMap = new Map();
      partnerSnap.docs.forEach((d) => {
        const merged = { ...d.data(), business_name: partnerBizMap.get(d.id) || '' };
        partnerMap.set(d.id, formatUserName(merged));
      });
      setPartners(partnerSnap.docs.map((d) => {
        const merged = { ...d.data(), business_name: partnerBizMap.get(d.id) || '' };
        return { id: d.id, name: formatUserName(merged) };
      }));

      // 결제 요청 — SR 매칭으로 카테고리 + 세부 유형 조회
      const reqs = await Promise.all(reqSnap.docs.map(async (d) => {
        const data = d.data();
        const at = data.created_at || data.createdAt;
        let category = '';
        let subType = '';
        if (data.service_request_id) {
          try {
            const srDoc = await getDoc(doc(db, 'service_requests', data.service_request_id));
            if (srDoc.exists()) {
              const srD = srDoc.data();
              category = srD.category || '';
              subType = srD.moving_type || srD.cleaning_type || srD.internet_type || srD.interior_style || '';
            }
          } catch { /* skip */ }
        }
        return {
          id: d.id,
          partnerId: data.partner_id || data.partnerId || '',
          partnerName: partnerMap.get(data.partner_id || data.partnerId) || '-',
          productName: data.product_name || data.productName || data.label || '-',
          category,
          subType,
          amount: data.amount || 0,
          method: data.method || 'transfer',
          memo: data.memo || '',
          status: data.status || 'pending',
          createdAt: at?.toDate ? at.toDate() : (at?.seconds ? new Date(at.seconds * 1000) : null),
        };
      }));
      reqs.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
      setPaymentRequests(reqs);

      // 미수
      const recvs = await Promise.all(recvSnap.docs.map(async (d) => {
        const data = d.data();
        let customerName = '-';
        let category = '-';
        if (data.service_request_id) {
          try {
            const srSnap = await getDoc(doc(db, 'service_requests', data.service_request_id));
            if (srSnap.exists()) {
              const sr = srSnap.data();
              category = sr.category || '-';
              if (sr.customer_id) {
                const cSnap = await getDoc(doc(db, 'customers', sr.customer_id));
                if (cSnap.exists()) customerName = cSnap.data().name || '-';
              }
            }
          } catch { /* skip */ }
        }
        // 세부 유형 (moving_type / cleaning_type / internet_type / interior_style)
        let subType = '';
        if (data.service_request_id) {
          try {
            const srSnap2 = await getDoc(doc(db, 'service_requests', data.service_request_id));
            if (srSnap2.exists()) {
              const sr2 = srSnap2.data();
              subType = sr2.moving_type || sr2.cleaning_type || sr2.internet_type || sr2.interior_style || '';
            }
          } catch { /* skip */ }
        }
        const at = data.created_at || data.createdAt;
        return {
          id: d.id,
          partnerId: data.partner_id || data.partnerId || '',
          partnerName: partnerMap.get(data.partner_id || data.partnerId) || '-',
          serviceRequestId: data.service_request_id || null,
          customerName,
          category,
          subType,
          amount: data.amount || 0,
          // 미수금은 결제 전이라 method 없음 — 결제완료(paid) 시에만 method 표기
          method: data.status === 'paid' ? (data.method || '카드') : '',
          status: data.status === 'paid' ? 'scheduled' : (data.status || 'pending'),
          createdAt: at?.toDate ? at.toDate() : (at?.seconds ? new Date(at.seconds * 1000) : null),
        };
      }));
      setReceivables(recvs.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)));
    } catch (err) {
      console.error('정산 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // 3분할 통계
  const headerStats = useMemo(() => {
    const thisMonth = new Date();
    thisMonth.setDate(1);
    thisMonth.setHours(0, 0, 0, 0);

    const outstandingThisMonth = receivables
      .filter((r) => r.createdAt && r.createdAt >= thisMonth && r.status !== 'scheduled')
      .reduce((s, r) => s + r.amount, 0);
    const paidCount = paymentRequests.filter((r) => r.status === 'completed').length;
    const todayPaidCount = paymentRequests.filter((r) => {
      if (r.status !== 'completed' || !r.createdAt) return false;
      const diff = Date.now() - r.createdAt.getTime();
      return diff < 24 * 60 * 60 * 1000;
    }).length;
    return { outstandingThisMonth, paidCount, todayPaidCount };
  }, [paymentRequests, receivables]);

  // 결제 요청 통계
  const reqStats = useMemo(() => {
    const pending = paymentRequests.filter((r) => r.status === 'pending').length;
    const completed = paymentRequests.filter((r) => r.status === 'completed').length;
    const rejected = paymentRequests.filter((r) => r.status === 'rejected').length;
    return { pending, completed, rejected };
  }, [paymentRequests]);

  const filteredReqs = useMemo(() => {
    const list = paymentRequests.filter((r) => reqStatusFilter === 'all' || r.status === reqStatusFilter);
    const getVal = (r) => {
      if (reqSortKey === 'partnerName') return (r.partnerName || '').toLowerCase();
      if (reqSortKey === 'productName') return (r.productName || '').toLowerCase();
      if (reqSortKey === 'amount') return r.amount || 0;
      if (reqSortKey === 'status') return REQ_STATUS_ORDER[r.status] ?? 99;
      return r.createdAt?.getTime() || 0;
    };
    return [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return reqSortDir === 'asc' ? -1 : 1;
      if (va > vb) return reqSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [paymentRequests, reqStatusFilter, reqSortKey, reqSortDir]);
  const pagedReqs = filteredReqs.slice((reqPage - 1) * PER_PAGE, reqPage * PER_PAGE);
  const totalReqPages = Math.max(1, Math.ceil(filteredReqs.length / PER_PAGE));

  const filteredRecv = useMemo(() => {
    const list = receivables.filter((r) => {
      if (recvStatusFilter !== 'all' && r.status !== recvStatusFilter) return false;
      if (recvMethodFilter !== 'all' && r.method !== recvMethodFilter) return false;
      return true;
    });
    const getVal = (r) => {
      if (recvSortKey === 'customerName') return (r.customerName || '').toLowerCase();
      if (recvSortKey === 'partnerName') return (r.partnerName || '').toLowerCase();
      if (recvSortKey === 'amount') return r.amount || 0;
      if (recvSortKey === 'status') return RECV_STATUS_ORDER[r.status] ?? 99;
      return r.createdAt?.getTime() || 0;
    };
    return [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (va < vb) return recvSortDir === 'asc' ? -1 : 1;
      if (va > vb) return recvSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [receivables, recvStatusFilter, recvMethodFilter, recvSortKey, recvSortDir]);
  const pagedRecv = filteredRecv.slice((recvPage - 1) * PER_PAGE, recvPage * PER_PAGE);
  const totalRecvPages = Math.max(1, Math.ceil(filteredRecv.length / PER_PAGE));

  const toggleReqSort = (key) => {
    if (reqSortKey === key) setReqSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setReqSortKey(key); setReqSortDir(key === 'createdAt' || key === 'amount' ? 'desc' : 'asc'); }
    setReqPage(1);
  };
  const toggleRecvSort = (key) => {
    if (recvSortKey === key) setRecvSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setRecvSortKey(key); setRecvSortDir(key === 'createdAt' || key === 'amount' ? 'desc' : 'asc'); }
    setRecvPage(1);
  };
  const reqSortInd = (key) => (
    <SortInd $active={reqSortKey === key}>{reqSortKey === key ? (reqSortDir === 'asc' ? '▲' : '▼') : '▲▼'}</SortInd>
  );
  const recvSortInd = (key) => (
    <SortInd $active={recvSortKey === key}>{recvSortKey === key ? (recvSortDir === 'asc' ? '▲' : '▼') : '▲▼'}</SortInd>
  );

  const totalOutstanding = receivables.filter((r) => r.status !== 'scheduled').reduce((s, r) => s + r.amount, 0);

  // 액션 — 본사 외상 청구: 상품명/금액 자유 입력, 미수금/수수료와 무관
  const openCreate = () => {
    setForm({
      partnerId: partners[0]?.id || '',
      productName: '',
      amount: '',
      method: 'transfer',
      memo: '',
      receivableIds: [],
    });
    setCreateModal(true);
  };

  const onPartnerChange = (partnerId) => {
    setForm((prev) => ({ ...prev, partnerId }));
  };

  const submitCreate = async () => {
    if (!form.partnerId) { alert('업체를 선택해주세요.'); return; }
    if (!form.productName.trim()) { alert('상품명을 입력해주세요.'); return; }
    if (!form.amount) { alert('금액을 입력해주세요.'); return; }
    try {
      await addDoc(collection(db, 'partner_payment_requests'), {
        partner_id: form.partnerId,
        product_name: form.productName.trim(),
        amount: Number(form.amount),
        method: form.method,
        memo: form.memo,
        label: form.productName.trim(),
        status: 'pending',
        receivable_ids: [],
        created_at: serverTimestamp(),
      });
      setCreateModal(false);
      fetchData();
    } catch (err) {
      console.error('요청 생성 실패:', err);
    }
  };

  const updateReqStatus = async (id, next) => {
    try {
      await updateDoc(doc(db, 'partner_payment_requests', id), { status: next, updated_at: serverTimestamp() });
      fetchData();
    } catch (err) {
      console.error('상태 변경 실패:', err);
    }
  };

  // 일괄 입금 확인 — 결제(입금) 방식 선택 모달을 먼저 띄움
  const bulkConfirmRecv = () => {
    if (recvSelected.length === 0) return;
    setBulkMethod('card');
    setBulkMethodModal(true);
  };

  // 선택한 입금 방식으로 일괄 입금확인 처리
  const submitBulkConfirm = async () => {
    if (recvSelected.length === 0) return;
    try {
      for (const id of recvSelected) {
        await updateDoc(doc(db, 'partner_receivables', id), {
          status: 'paid',
          method: bulkMethod,
          updated_at: serverTimestamp(),
        });
      }
      setBulkMethodModal(false);
      setRecvSelected([]);
      fetchData();
    } catch (err) {
      console.error('일괄 처리 실패:', err);
    }
  };

  const toggleRecvSelect = (id) => {
    setRecvSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleRecvAll = () => {
    if (pagedRecv.every((r) => recvSelected.includes(r.id))) {
      setRecvSelected((prev) => prev.filter((id) => !pagedRecv.some((r) => r.id === id)));
    } else {
      setRecvSelected((prev) => Array.from(new Set([...prev, ...pagedRecv.map((r) => r.id)])));
    }
  };

  const StatusText = ({ meta, status }) => {
    const s = meta[status];
    if (!s) return <span>-</span>;
    return <span style={{ color: s.color, fontWeight: 500 }}>{s.label}</span>;
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <Container>
      <Title>제휴업체 정산 (DB 구매 내역)</Title>
      <SubNote>본사와 제휴업체 간의 DB 구매/서비스 미수 정산 내역을 관리합니다.</SubNote>

      <StatsRow>
        <StatCard>
          <StatLabel>이번달 미수금액</StatLabel>
          <StatValue $color="#EF4444">{headerStats.outstandingThisMonth.toLocaleString()}원</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>결제 건수 (누적)</StatLabel>
          <StatValue>{headerStats.paidCount}건</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>24시간 수금 건수</StatLabel>
          <StatValue $color="#22C55E">{headerStats.todayPaidCount}건</StatValue>
        </StatCard>
      </StatsRow>

      <NoticeBox>
        입금계좌 선택 후 이사선 인덕 업무가 가능하며, 고정 월수 열람금 · 결제 수단 조건도 함께 설정할 수 있습니다.
      </NoticeBox>

      <Tabs>
        <TabBtn $active={tab === 'requests'} onClick={() => setTab('requests')}>결제 요청</TabBtn>
        <TabBtn $active={tab === 'receivables'} onClick={() => setTab('receivables')}>미수</TabBtn>
      </Tabs>

      {tab === 'requests' && (
        <>
          <SubStatsRow>
            <SubStatCard>
              <SubStatLabel>대기</SubStatLabel>
              <SubStatValue $color="#F59E0B">{reqStats.pending}건</SubStatValue>
            </SubStatCard>
            <SubStatCard>
              <SubStatLabel>완료</SubStatLabel>
              <SubStatValue $color="#22C55E">{reqStats.completed}건</SubStatValue>
            </SubStatCard>
            <SubStatCard>
              <SubStatLabel>거절 · 연체</SubStatLabel>
              <SubStatValue $color="#EF4444">{reqStats.rejected}건</SubStatValue>
            </SubStatCard>
          </SubStatsRow>

          <ToolRow>
            <Select value={reqStatusFilter} onChange={(e) => { setReqStatusFilter(e.target.value); setReqPage(1); }}>
              <option value="all">상태 전체</option>
              {Object.entries(REQ_STATUS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </Select>
            <ToolRight>
              <PrimaryBtn onClick={openCreate}>결제 요청 생성</PrimaryBtn>
            </ToolRight>
          </ToolRow>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th $sortable $active={reqSortKey === 'partnerName'} onClick={() => toggleReqSort('partnerName')}>대표자(업체명){reqSortInd('partnerName')}</Th>
                  <Th>유형</Th>
                  <Th $sortable $active={reqSortKey === 'productName'} onClick={() => toggleReqSort('productName')}>상품명{reqSortInd('productName')}</Th>
                  <Th $sortable $active={reqSortKey === 'amount'} onClick={() => toggleReqSort('amount')} style={{ textAlign: 'right' }}>금액{reqSortInd('amount')}</Th>
                  <Th>결제방법</Th>
                  <Th>메모</Th>
                  <Th $sortable $active={reqSortKey === 'status'} onClick={() => toggleReqSort('status')}>상태{reqSortInd('status')}</Th>
                  <Th $sortable $active={reqSortKey === 'createdAt'} onClick={() => toggleReqSort('createdAt')}>요청 일시{reqSortInd('createdAt')}</Th>
                  <Th>액션</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><Td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>로딩 중...</Td></tr>
                ) : pagedReqs.length === 0 ? (
                  <tr><Td colSpan={9} style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>결제 요청이 없습니다.</Td></tr>
                ) : pagedReqs.map((r) => (
                  <Tr key={r.id}>
                    <Td>{r.partnerName}</Td>
                    <Td style={{ color: '#6B7280' }}>{labelType(r.category, r.subType)}</Td>
                    <Td style={{ fontWeight: 500 }}>{r.productName}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 600 }}>{r.amount.toLocaleString()}원</Td>
                    <Td>{(r.method === 'card' || r.method === '카드') ? '카드' : '계좌이체'}</Td>
                    <Td style={{ color: '#6B7280' }}>{r.memo || '-'}</Td>
                    <Td><StatusText meta={REQ_STATUS} status={r.status} /></Td>
                    <Td>{fmtDate(r.createdAt)}</Td>
                    <Td>
                      {r.status === 'pending' && (
                        <InlineActions>
                          <InlineBtn onClick={() => updateReqStatus(r.id, 'completed')}>승인</InlineBtn>
                          <InlineBtnDanger onClick={() => updateReqStatus(r.id, 'rejected')}>거절</InlineBtnDanger>
                        </InlineActions>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <PageFoot>
            <PageInfo>페이지 {reqPage} / {totalReqPages} · 총 {filteredReqs.length}건</PageInfo>
            <Pagination current={reqPage} total={totalReqPages} onChange={setReqPage} />
          </PageFoot>
        </>
      )}

      {tab === 'receivables' && (
        <>
          <BigAmountRow>
            <BigAmountLabel>전체 미수 금액</BigAmountLabel>
            <BigAmountValue>{totalOutstanding.toLocaleString()}원</BigAmountValue>
          </BigAmountRow>

          <ToolRow>
            <Select value={recvStatusFilter} onChange={(e) => { setRecvStatusFilter(e.target.value); setRecvPage(1); }}>
              <option value="all">상태 전체</option>
              {Object.entries(RECV_STATUS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </Select>
            <Select value={recvMethodFilter} onChange={(e) => { setRecvMethodFilter(e.target.value); setRecvPage(1); }}>
              <option value="all">결제방법 전체</option>
              <option value="card">카드</option>
              <option value="transfer">계좌이체</option>
            </Select>
            <SelectCount>선택 {recvSelected.length}건</SelectCount>
            <ToolRight>
              <ActionBtn onClick={bulkConfirmRecv}>일괄 입금 확인</ActionBtn>
            </ToolRight>
          </ToolRow>

          {filteredRecv.length === 0 ? (
            <EmptyYellow>해당 조건의 미수가 없습니다.</EmptyYellow>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={pagedRecv.length > 0 && pagedRecv.every((r) => recvSelected.includes(r.id))}
                        onChange={toggleRecvAll}
                      />
                    </Th>
                    <Th $sortable $active={recvSortKey === 'customerName'} onClick={() => toggleRecvSort('customerName')}>고객명{recvSortInd('customerName')}</Th>
                    <Th>유형</Th>
                    <Th $sortable $active={recvSortKey === 'partnerName'} onClick={() => toggleRecvSort('partnerName')}>대표자(업체명){recvSortInd('partnerName')}</Th>
                    <Th $sortable $active={recvSortKey === 'createdAt'} onClick={() => toggleRecvSort('createdAt')}>발생 일시{recvSortInd('createdAt')}</Th>
                    <Th $sortable $active={recvSortKey === 'amount'} onClick={() => toggleRecvSort('amount')} style={{ textAlign: 'right' }}>금액{recvSortInd('amount')}</Th>
                    <Th>결제방법</Th>
                    <Th $sortable $active={recvSortKey === 'status'} onClick={() => toggleRecvSort('status')}>상태{recvSortInd('status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRecv.map((r) => (
                    <Tr key={r.id}>
                      <Td>
                        <input
                          type="checkbox"
                          checked={recvSelected.includes(r.id)}
                          onChange={() => toggleRecvSelect(r.id)}
                        />
                      </Td>
                      <Td>{r.customerName}</Td>
                      <Td>{labelType(r.category, r.subType)}</Td>
                      <Td>{r.partnerName}</Td>
                      <Td>{fmtDate(r.createdAt)}</Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600 }}>{r.amount.toLocaleString()}원</Td>
                      <Td>{r.status === 'pending' ? '-' : ((r.method === 'card' || r.method === '카드') ? '카드' : '계좌이체')}</Td>
                      <Td><StatusText meta={RECV_STATUS} status={r.status} /></Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}

          <PageFoot>
            <PageInfo>페이지 {recvPage} / {totalRecvPages} · 총 {filteredRecv.length}건</PageInfo>
            <Pagination current={recvPage} total={totalRecvPages} onChange={setRecvPage} />
          </PageFoot>
        </>
      )}

      {createModal && (
        <ModalOverlay onClick={() => setCreateModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>결제 요청 생성</ModalTitle>
              <XBtn onClick={() => setCreateModal(false)}><IoClose size={22} /></XBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>업체 선택</FieldLabel>
              <Select value={form.partnerId} onChange={(e) => onPartnerChange(e.target.value)}>
                <option value="">업체를 선택하세요</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>

              <FieldLabel style={{ marginTop: 14 }}>상품명</FieldLabel>
              <Input
                value={form.productName}
                onChange={(e) => setForm({ ...form, productName: e.target.value })}
                placeholder="예: 4월 수수료, 광고비"
              />

              <FieldLabel style={{ marginTop: 14 }}>금액</FieldLabel>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0"
              />

              <FieldLabel style={{ marginTop: 14 }}>결제 설정</FieldLabel>
              <RadioRow>
                <RadioLabel>
                  <input type="radio" name="method" checked={form.method === 'transfer'} onChange={() => setForm({ ...form, method: 'transfer' })} />
                  계좌이체
                </RadioLabel>
                <RadioLabel>
                  <input type="radio" name="method" checked={form.method === 'card'} onChange={() => setForm({ ...form, method: 'card' })} />
                  카드
                </RadioLabel>
              </RadioRow>

              <FieldLabel style={{ marginTop: 14 }}>메모</FieldLabel>
              <Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={() => setCreateModal(false)}>취소</GhostBtn>
              <PrimaryBtn onClick={submitCreate}>요청 생성</PrimaryBtn>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {bulkMethodModal && (
        <ModalOverlay onClick={() => setBulkMethodModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>일괄 입금 확인</ModalTitle>
              <XBtn onClick={() => setBulkMethodModal(false)}><IoClose size={22} /></XBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>선택한 {recvSelected.length}건의 입금(결제) 방식을 선택하세요.</FieldLabel>
              <RadioRow>
                <RadioLabel>
                  <input type="radio" name="bulkMethod" checked={bulkMethod === 'card'} onChange={() => setBulkMethod('card')} />
                  카드
                </RadioLabel>
                <RadioLabel>
                  <input type="radio" name="bulkMethod" checked={bulkMethod === 'transfer'} onChange={() => setBulkMethod('transfer')} />
                  계좌이체
                </RadioLabel>
              </RadioRow>
            </ModalBody>
            <ModalFooter>
              <GhostBtn onClick={() => setBulkMethodModal(false)}>취소</GhostBtn>
              <PrimaryBtn onClick={submitBulkConfirm}>입금 확인 처리</PrimaryBtn>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default PartnerReceivable;

// --- Styled ---
const Container = styled.div``;
const Title = styled.h1`font-size:18px;font-weight:600;margin:0 0 4px;`;
const SubNote = styled.p`font-size:13px;color:${({theme})=>theme.colors.textSecondary};margin:0 0 16px;`;

const StatsRow = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:14px;`;
const StatCard = styled.div`background:#fff;padding:18px 20px;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const StatLabel = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:6px;`;
const StatValue = styled.div`font-size:20px;font-weight:700;color:${({$color})=>$color||'#1F2937'};`;

const NoticeBox = styled.div`
  background:#FFFBEB;
  color:#713F12;
  border:1px solid #FDE68A;
  border-radius:${({theme})=>theme.radius.sm};
  padding:10px 14px;
  font-size:13px;
  line-height:1.5;
  margin-bottom:16px;
`;

const Tabs = styled.div`display:flex;gap:8px;border-bottom:1px solid ${({theme})=>theme.colors.border};margin-bottom:16px;`;
const TabBtn = styled.button`
  padding:10px 20px;border:none;background:none;
  font-size:13px;font-weight:${({$active})=>$active?600:400};
  color:${({$active,theme})=>$active?theme.colors.primary:theme.colors.textSecondary};
  border-bottom:2px solid ${({$active,theme})=>$active?theme.colors.primary:'transparent'};
  margin-bottom:-1px;cursor:pointer;
`;

const SubStatsRow = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:12px;`;
const SubStatCard = styled.div`background:#fff;padding:14px 16px;border-radius:${({theme})=>theme.radius.md};border:1px solid ${({theme})=>theme.colors.borderLight};`;
const SubStatLabel = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};margin-bottom:4px;`;
const SubStatValue = styled.div`font-size:17px;font-weight:700;color:${({$color})=>$color||'#1F2937'};`;

const ToolRow = styled.div`display:flex;gap:8px;align-items:center;margin-bottom:12px;flex-wrap:wrap;`;
const Select = styled.select`padding:8px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;background:#fff;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;
const SelectCount = styled.span`font-size:13px;color:${({theme})=>theme.colors.textSecondary};`;
const ToolRight = styled.div`margin-left:auto;display:flex;gap:6px;`;

const ActionBtn = styled.button`padding:8px 14px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:13px;color:${({theme})=>theme.colors.text};cursor:pointer;&:hover{background:#F9FAFB;}`;
const PrimaryBtn = styled.button`padding:8px 14px;border:none;background:${({theme})=>theme.colors.primary};color:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:13px;font-weight:500;cursor:pointer;`;

const TableWrapper = styled.div`overflow-x:auto;background:#fff;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`
  background:#F9FAFB;
  padding:12px 14px;
  text-align:left;
  font-size:12px;
  font-weight:500;
  color:${({theme,$active})=>$active?theme.colors.primary:theme.colors.textSecondary};
  border-bottom:1px solid ${({theme})=>theme.colors.border};
  white-space:nowrap;
  ${({$sortable,theme})=>$sortable&&`
    cursor:pointer;
    user-select:none;
    &:hover { color: ${theme.colors.primary}; }
  `}
`;
const SortInd = styled.span`
  display:inline-block;
  margin-left:6px;
  color:${({theme,$active})=>$active?theme.colors.primary:'#C0C7D1'};
  font-size:10px;
  line-height:1;
  vertical-align:middle;
`;
const Tr = styled.tr`&:not(:last-child) td{border-bottom:1px solid ${({theme})=>theme.colors.borderLight};}`;
const Td = styled.td`padding:12px 14px;font-size:13px;color:${({theme})=>theme.colors.text};white-space:nowrap;`;

const InlineActions = styled.div`display:flex;gap:6px;`;
const InlineBtn = styled.button`padding:4px 10px;border:1px solid ${({theme})=>theme.colors.success};background:#fff;color:${({theme})=>theme.colors.success};border-radius:${({theme})=>theme.radius.sm};font-size:12px;cursor:pointer;&:hover{background:#F0FDF4;}`;
const InlineBtnDanger = styled.button`padding:4px 10px;border:1px solid ${({theme})=>theme.colors.danger};background:#fff;color:${({theme})=>theme.colors.danger};border-radius:${({theme})=>theme.radius.sm};font-size:12px;cursor:pointer;&:hover{background:#FEF2F2;}`;

const BigAmountRow = styled.div`background:#fff;padding:20px 22px;border-radius:${({theme})=>theme.radius.md};box-shadow:${({theme})=>theme.shadow.sm};margin-bottom:14px;display:flex;align-items:baseline;justify-content:space-between;`;
const BigAmountLabel = styled.div`font-size:13px;color:${({theme})=>theme.colors.textSecondary};`;
const BigAmountValue = styled.div`font-size:28px;font-weight:800;color:#EF4444;`;

const EmptyYellow = styled.div`
  background:#FFFBEB;
  color:#713F12;
  border:1px solid #FDE68A;
  border-radius:${({theme})=>theme.radius.sm};
  padding:20px;
  text-align:center;
  font-size:13px;
`;

const PageFoot = styled.div`display:flex;align-items:center;justify-content:space-between;`;
const PageInfo = styled.div`font-size:12px;color:${({theme})=>theme.colors.textSecondary};`;

// Modal
const ModalOverlay = styled.div`position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:1000;`;
const ModalContent = styled.div`background:#fff;border-radius:${({theme})=>theme.radius.lg};width:100%;max-width:480px;box-shadow:${({theme})=>theme.shadow.lg};`;
const ModalHeader = styled.div`display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid ${({theme})=>theme.colors.border};`;
const ModalTitle = styled.h2`font-size:16px;font-weight:600;margin:0;`;
const XBtn = styled.button`background:none;border:none;color:${({theme})=>theme.colors.textLight};cursor:pointer;display:inline-flex;align-items:center;`;
const ModalBody = styled.div`padding:22px;`;
const ModalFooter = styled.div`display:flex;justify-content:flex-end;gap:8px;padding:14px 22px;border-top:1px solid ${({theme})=>theme.colors.border};`;
const FieldLabel = styled.div`font-size:13px;font-weight:500;color:${({theme})=>theme.colors.text};margin-bottom:6px;`;
const Input = styled.input`width:100%;padding:9px 12px;border:1px solid ${({theme})=>theme.colors.border};border-radius:${({theme})=>theme.radius.sm};font-size:13px;&:focus{outline:none;border-color:${({theme})=>theme.colors.primary};}`;
const RadioRow = styled.div`display:flex;gap:18px;`;
const RadioLabel = styled.label`display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer;`;
const GhostBtn = styled.button`padding:8px 18px;border:1px solid ${({theme})=>theme.colors.border};background:#fff;border-radius:${({theme})=>theme.radius.sm};font-size:13px;cursor:pointer;`;
